import { useState } from 'react';
import { FiMail, FiPhone, FiSend, FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { contactConfig } from '../config/contact';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:7890/api';

/**
 * Botón flotante de WhatsApp
 */
function WhatsAppButton({ whatsapp }) {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hola, me gustaría obtener más información sobre Shalom.');
    const url = `https://wa.me/${whatsapp}?text=${message}`;
    window.open(url, '_blank');
  };

  return (
    <button
      onClick={handleWhatsAppClick}
      className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-all transform hover:scale-110 z-50 animate-pulse hover:animate-none"
      aria-label="Contactar por WhatsApp"
      title="Enviar mensaje por WhatsApp"
    >
      <FaWhatsapp className="w-8 h-8" />
    </button>
  );
}

/**
 * Página de Contacto
 */
export default function Contacto() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const contactInfo = [
    {
      icon: FiMail,
      titulo: 'Email',
      valor: contactConfig.email,
      descripcion: 'Envíanos un correo',
      link: `mailto:${contactConfig.email}`
    },
    {
      icon: FiPhone,
      titulo: 'Teléfono',
      valor: contactConfig.phone,
      descripcion: 'Llámanos en horario de atención',
      link: `tel:${contactConfig.phone.replace(/\s/g, '')}`
    },
    {
      icon: FiClock,
      titulo: 'Horario',
      valor: contactConfig.schedule,
      descripcion: 'Tiempo de Ecuador (GMT-5)'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar mensajes de estado al escribir
    if (status.success || status.error) {
      setStatus({ loading: false, success: false, error: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setStatus({
        loading: false,
        success: false,
        error: 'Por favor completa todos los campos obligatorios'
      });
      return;
    }

    setStatus({ loading: true, success: false, error: null });

    try {
      const response = await axios.post(`${API_URL}/contact/send`, formData);

      setStatus({
        loading: false,
        success: true,
        error: null
      });

      // Limpiar formulario
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });

      // Auto-limpiar mensaje de éxito después de 5 segundos
      setTimeout(() => {
        setStatus({ loading: false, success: false, error: null });
      }, 5000);

    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      setStatus({
        loading: false,
        success: false,
        error: error.response?.data?.details || 'Error al enviar el mensaje. Por favor intenta nuevamente.'
      });
    }
  };

  return (
    <>
      <div className="min-h-full bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Contacto
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Estamos aquí para ayudarte. Si tienes preguntas, comentarios o sugerencias,
              no dudes en ponerte en contacto con nosotros.
            </p>
          </div>

          {/* Información de contacto */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactInfo.map((info) => {
              const Icon = info.icon;
              return (
                <div
                  key={info.titulo}
                  className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {info.titulo}
                  </h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-blue-600 font-medium mb-2 hover:text-blue-700 transition-colors inline-block"
                    >
                      {info.valor}
                    </a>
                  ) : (
                    <p className="text-blue-600 font-medium mb-2">
                      {info.valor}
                    </p>
                  )}
                  <p className="text-sm text-gray-600">
                    {info.descripcion}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Formulario de contacto */}
          <div className="bg-white rounded-xl shadow-md p-8 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Envíanos un mensaje
            </h2>

            {/* Mensajes de estado */}
            {status.success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-900 font-medium">¡Mensaje enviado exitosamente!</p>
                  <p className="text-green-700 text-sm mt-1">
                    Nos pondremos en contacto contigo lo antes posible.
                  </p>
                </div>
              </div>
            )}

            {status.error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-900 font-medium">Error al enviar el mensaje</p>
                  <p className="text-red-700 text-sm mt-1">{status.error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Teléfono (opcional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="+593 99 123 4567"
                />
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Asunto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="¿En qué podemos ayudarte?"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mensaje <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={2000}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder="Escribe tu mensaje aquí..."
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/2000 caracteres
                </p>
              </div>

              <button
                type="submit"
                disabled={status.loading}
                className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status.loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <FiSend className="w-5 h-5" />
                    Enviar Mensaje
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Botón flotante de WhatsApp */}
      <WhatsAppButton whatsapp={contactConfig.whatsapp} />
    </>
  );
}
